import React,{useEffect,useCallback,useState,useRef} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import {useSiteContext} from '../../context/SiteContext';
import SafeIcon from '../../common/SafeIcon';
import activityTracker from '../../lib/activityTracker';
import { validPreviewUrl } from '../../lib/previewUrl';

const ReferenceModal=()=> {
  const {isModalOpen,closeModal,selectedReference,requestSimilar}=useSiteContext();
  const [device,setDevice]=useState('desktop');
  const [frameLoaded,setFrameLoaded]=useState(false);
  const closeButtonRef=useRef(null);
  useEffect(()=> {
    setFrameLoaded(false);
    const width=window.innerWidth;
    setDevice(width < 640 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop');
  },[selectedReference?.websiteId]);
  useEffect(()=> { if (isModalOpen) closeButtonRef.current?.focus(); },[isModalOpen]);

  const handleClose=useCallback(()=> {
    if (selectedReference) {
      activityTracker.trackModalClosed(selectedReference.websiteId);
    }
    closeModal();
  },[selectedReference,closeModal]);

  useEffect(()=> {
    const handleKeyDown=(e)=> {
      if (e.key==='Escape') handleClose();
    };
    if (isModalOpen) {
      document.body.style.overflow='hidden';
      window.addEventListener('keydown',handleKeyDown);
    } else {
      document.body.style.overflow='unset';
    }
    return ()=> {
      document.body.style.overflow='unset';
      window.removeEventListener('keydown',handleKeyDown);
    };
  },[isModalOpen,handleClose]);

  if (!isModalOpen || !selectedReference) return null;

  const previewSource=validPreviewUrl(selectedReference.previewUrl) || validPreviewUrl(selectedReference.websiteUrl);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4 lg:p-6">
        <motion.div
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          transition={{duration: 0.3}}
          onClick={handleClose}
          className="absolute inset-0 bg-black/85 backdrop-blur-sm cursor-pointer"
          aria-hidden="true"
        />
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          initial={{opacity: 0,scale: 0.96,y: 10}}
          animate={{opacity: 1,scale: 1,y: 0}}
          exit={{opacity: 0,scale: 0.96,y: 10}}
          transition={{duration: 0.3,ease: "easeOut"}}
          className="relative w-[100vw] h-[100dvh] sm:w-[98vw] sm:h-[96dvh] md:max-w-[1700px] xl:max-w-[1900px] 2xl:max-w-[2200px] bg-[#111119]/95 backdrop-blur-xl border border-white/10 rounded-none sm:rounded-3xl shadow-[0_30px_120px_rgba(0,0,0,0.65)] flex flex-col overflow-hidden"
        >
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-20 -left-20 w-64 h-64 bg-blue-600/10 rounded-full blur-[80px]" />
            <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-purple-600/10 rounded-full blur-[80px]" />
          </div>
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-white/10 bg-gradient-to-r from-white/[0.07] to-white/[0.03] shrink-0 relative z-10">
            <div className="min-w-0 flex-1 pr-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-blue-500/20 border border-blue-500/30 rounded text-blue-300">
                  Website System View
                </span>
              </div>
              <h2 id="modal-title" className="text-base sm:text-lg font-bold text-white truncate">
                {selectedReference.publicTitle}
              </h2>
              <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-slate-400 mt-1">
                <span className="flex items-center gap-1 min-w-0">
                  <SafeIcon name="Briefcase" className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{selectedReference.businessType}</span>
                </span>
                <span className="w-1 h-1 rounded-full bg-slate-600 hidden sm:block" />
                <span className="hidden sm:flex items-center gap-1 min-w-0">
                  <SafeIcon name="MapPin" className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{selectedReference.location}</span>
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 shrink-0">
              <button
                onClick={requestSimilar}
                className="hidden sm:flex px-4 sm:px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                Request Similar
              </button>
              <button
                onClick={handleClose}
                ref={closeButtonRef}
                aria-label="Close preview"
                className="p-2 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/30"
              >
                <SafeIcon name="X" className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 px-3 py-2 border-b border-white/10 bg-[#151822] shrink-0" role="group" aria-label="Website viewport">
            {['desktop','tablet','mobile'].map(mode => (
              <button key={mode} type="button" aria-pressed={device===mode} onClick={()=>{setDevice(mode);}} className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${device===mode ? 'bg-blue-600 text-white' : 'text-slate-300 bg-white/5 hover:bg-white/10'}`}>{mode}</button>
            ))}
          </div>
          <div className="flex-grow min-h-0 bg-[#090c14] relative overflow-auto p-0 sm:p-3 flex justify-center">
            {previewSource ? (
              <div className="relative h-full shrink-0 min-w-0 w-full rounded-none sm:rounded-xl overflow-hidden border border-white/10 shadow-2xl transition-[width] duration-200" style={{width:device==='mobile'?'min(100%, 390px)':device==='tablet'?'min(100%, 768px)':'100%'}}>
                <div className="h-8 bg-[#222838] flex items-center gap-1.5 px-3 border-b border-white/10">
                  <span className="w-2 h-2 rounded-full bg-red-400"/><span className="w-2 h-2 rounded-full bg-amber-400"/><span className="w-2 h-2 rounded-full bg-green-400"/>
                  <span className="ml-4 text-xs text-slate-400">Interactive website</span>
                </div>
                {!frameLoaded && <div className="absolute inset-x-0 top-8 bottom-0 flex items-center justify-center bg-[#111827] text-sm text-slate-300">Loading website experience…</div>}
                <iframe
                  key={selectedReference.websiteId}
                  src={previewSource}
                  onLoad={()=>setFrameLoaded(true)}
                  title={`Preview of ${selectedReference.publicTitle}`}
                  className="w-full border-0 bg-white"
                  style={{height:'calc(100% - 32px)'}}
                  sandbox="allow-scripts allow-same-origin allow-forms"
                  referrerPolicy="strict-origin-when-cross-origin"
                  loading="eager"
                />
              </div>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 p-8 text-center">
                <SafeIcon name="Monitor" className="w-12 h-12 mb-4 opacity-50" />
                <p className="text-lg font-medium text-slate-300">Page reference unavailable</p>
                <p className="text-sm mt-2 max-w-md">
                  This website reference is temporarily unavailable. Please try another reference or request a similar website.
                </p>
              </div>
            )}
          </div>
          <div className="sm:hidden p-4 border-t border-white/10 bg-[#1a1a24] shrink-0 relative z-10">
            <button
              onClick={requestSimilar}
              className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Request Similar
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ReferenceModal;